
// Pure profile+features -> CSV pipeline (task: "Selve CSV-generatoren bør i
// princippet kun have ansvar for: profil + features -> outputrækker ->
// CSV"). No knowledge of PointView tools/labels lives here - every actual
// value comes from dataSources.js's generic resolver. Used identically by
// the final export AND the editor's live preview (task: "Der må ikke være
// én formatteringslogik til preview og en anden til download") - both call
// generateRows()/buildCsvText() with the same profile+features, preview
// only adding `options.maxRows`.
import {resolveSourceValue, buildVertexContexts, discoverAttributeKeys, isCoordinateGeometryField, isMeasuredSource} from "./dataSources.js";

// Expands any {kind:"attributes-all"} column into one concrete
// {kind:"attribute", key} column per key actually present across the
// features being exported (see CsvProfile.js's doc comment on why this is
// a first-class column kind, not a standard-profile-only special case).
// Every other column passes through unchanged. Must run once, before both
// the header and the rows are built, so they stay in sync.
export function expandColumns(columns, features){
	const expanded = [];
	for(const column of columns){
		if(column.source && column.source.kind === "attributes-all"){
			for(const key of discoverAttributeKeys(features)){
				expanded.push({...column, id: `${column.id}:${key}`, header: key, source: {kind: "attribute", key}});
			}
		}else{
			expanded.push(column);
		}
	}
	return expanded;
}

function isMissing(raw){
	if(raw === undefined || raw === null) return true;
	if(typeof raw === "number" && !Number.isFinite(raw)) return true; // NaN/Infinity/-Infinity
	if(typeof raw === "string" && raw === "") return true;
	return false;
}

// Never JSON.stringify a plain array/object down to "[object Object]" -
// task's own explicit "må ikke producere... [object Object]". A handful of
// existing tool attributes ARE plain arrays (e.g. Rør's axisPoint/axisDir,
// stored as [x,y,z] - see pipeTool.js) - JSON.stringify gives a readable,
// lossless, deterministic textual value instead.
function stringifyComplex(raw, measured){
	try{
		// målte arrays (fx axisPoint = [x,y,z]) afrundes til højst MAX_DECIMALS; andre arrays/objekter skrives uændret
		if(!measured) return JSON.stringify(raw);
		return JSON.stringify(raw, (key, value) => (typeof value === "number" && Number.isFinite(value) ? Number(value.toFixed(MAX_DECIMALS)) : value));
	}catch(e){
		return String(raw);
	}
}

// Bruger-krav (2026-10): højst 4 decimaler i geometrisk beregnede/målte værdier - koordinater (X/Y/Z, start/slut/centroide,
// axisPoint), længde, areal, omkreds, radius/diameter, koter, vinkler (se isMeasuredSource i dataSources.js). Almindelige
// attributter/properties rundes IKKE af, bare fordi de er numeriske. (Ellers kommer flydende-komma-støj som 575525.0815900398.)
// Uden "Koordinatdecimaler" afrundes til højst 4, og overflødige nuller skrives ikke ("1.5", ikke "1.5000"); en sat
// "Koordinatdecimaler" (0-4) gælder koordinatfelterne og skrives med faste decimaler. Kun teksten afrundes - geometrien røres aldrig.
export const MAX_DECIMALS = 4;

function formatNumber(value, profile, coordinate, measured){
	let text;
	if(coordinate && Number.isFinite(profile.coordinatePrecision)){
		text = value.toFixed(Math.max(0, Math.min(MAX_DECIMALS, Math.floor(profile.coordinatePrecision))));
	}else if(measured){
		text = String(Number(value.toFixed(MAX_DECIMALS))); // Number(): fjerner afsluttende nuller og "-0"
	}else{
		text = String(value); // almindelig attribut/konstant: oprindelig værdi og præcision
	}
	const decimalSeparator = profile.decimalSeparator || ".";
	if(decimalSeparator !== "."){
		// A formatted number has at most one "." (toFixed/String never
		// produce more than one decimal point), so a single non-global
		// replace is exact and can't corrupt anything else in the string
		// (e.g. a "-" sign, or exponential notation's "e").
		text = text.replace(".", decimalSeparator);
	}
	return text;
}

// Resolves one column for one row context, applying missing-value fallback
// and profile-driven number formatting - the one place both concerns live,
// so csvGenerator's row-building loop stays free of per-type branching.
export function resolveColumnValue(column, ctx, profile){
	const raw = resolveSourceValue(column.source, ctx);
	if(isMissing(raw)){
		return column.fallback === undefined || column.fallback === null ? "" : String(column.fallback);
	}
	const measured = isMeasuredSource(column.source);
	if(typeof raw === "number"){
		const coordinate = column.source.kind === "geometry" && isCoordinateGeometryField(column.source.field);
		return formatNumber(raw, profile, coordinate, measured);
	}
	if(typeof raw === "object") return stringifyComplex(raw, measured);
	return String(raw);
}

// options.maxRows: stop generating once this many DATA rows exist (task:
// "Preview skal kun generere den begrænsede datamængde, der er nødvendig
// for visningen") - both the final export (no cap) and the live preview
// (small cap) go through this exact same function, never two code paths.
export function generateRows(profile, features, options = {}){
	const columns = expandColumns(profile.columns, features);
	const header = profile.header ? columns.map((c) => c.header) : null;
	const rows = [];
	const maxRows = options.maxRows;

	for(const feature of features){
		if(profile.outputModel === "vertex"){
			for(const vertex of buildVertexContexts(feature)){
				const rowNumber = rows.length + 1; // fortløbende nummer (datakilde "rownumber")
				rows.push(columns.map((c) => resolveColumnValue(c, {feature, vertex, rowNumber}, profile)));
				if(maxRows != null && rows.length >= maxRows) return {header, rows};
			}
		}else{
			const rowNumber = rows.length + 1;
			rows.push(columns.map((c) => resolveColumnValue(c, {feature, vertex: null, rowNumber}, profile)));
			if(maxRows != null && rows.length >= maxRows) return {header, rows};
		}
	}
	return {header, rows};
}

// RFC4180-style escaping, parameterised on the profile's own separator (so
// ";"/","/tab are all handled the same way) - quote only when the cell
// actually contains the separator, a quote character, or a line break;
// double up any embedded quote. Never depends on browser locale.
function escapeCell(value, separator){
	const s = String(value);
	if(s.includes(separator) || s.includes('"') || s.includes("\n") || s.includes("\r")){
		return `"${s.replace(/"/g, '""')}"`;
	}
	return s;
}

export function serializeCsv(header, rows, profile){
	const separator = profile.separator || ",";
	const lines = [];
	if(header) lines.push(header.map((h) => escapeCell(h, separator)).join(separator));
	for(const row of rows) lines.push(row.map((v) => escapeCell(v, separator)).join(separator));
	return lines.join("\r\n");
}

// Full pipeline, encoding included (task: "UTF-8 BOM skal være en reel
// outputmulighed og ikke kun et UI-label") - a real U+FEFF prefix on the
// returned text, not just a Blob-type label.
const UTF8_BOM = "﻿";
export function buildCsvText(profile, features, options){
	const {header, rows} = generateRows(profile, features, options);
	const text = serializeCsv(header, rows, profile);
	return profile.encoding === "utf8bom" ? UTF8_BOM + text : text;
}
